import * as React from 'react';
import { generatePath, useNavigate } from 'react-router';
import { Search } from 'lucide-react';
import { useRoleRoutes } from '@/routing';
import { Button } from '@/components/ui/button';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import type { PageRoute } from '@/routing/types';
import { toast } from 'sonner';
export function CommandMenu() {
  const navigate = useNavigate();
  const subjectId = localStorage.getItem('currentSubjectId');
  const [open, setOpen] = React.useState(false);
  const { comboboxRoutes } = useRoleRoutes();

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || e.key === '/') {
        if (
          (e.target instanceof HTMLElement && e.target.isContentEditable) ||
          e.target instanceof HTMLInputElement ||
          e.target instanceof HTMLTextAreaElement ||
          e.target instanceof HTMLSelectElement
        ) {
          return;
        }

        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const runCommand = React.useCallback((command: () => unknown) => {
    setOpen(false);
    command();
  }, []);

  const navigateToRoute = (route: string) => {
    if (route.includes(':subjectId')) {
      if (!subjectId) {
        toast.error('Uhh Ohh , You should have a subjectId');
        return;
      }
      navigate(generatePath(route, { subjectId }));
    } else {
      navigate(route);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        className={cn(
          'relative h-9 w-full justify-start rounded-[0.5rem] bg-background text-sm font-normal text-muted-foreground shadow-none sm:pr-12 md:w-40 lg:w-64'
        )}
        onClick={() => setOpen(true)}
      >
        <Search className="mr-2 h-4 w-4" />
        <span className="hidden lg:inline-flex">Search pages...</span>
        <span className="inline-flex lg:hidden">Search...</span>
        <kbd className="pointer-events-none absolute right-[0.3rem] top-[0.3rem] hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Type a command or search..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup>
            {comboboxRoutes.map((route) => {
              const pageRoute = route as PageRoute;
              const Icon = pageRoute.icon;
              return (
                <CommandItem
                  key={pageRoute.path + Math.random()}
                  value={pageRoute.name}
                  onSelect={() => {
                    runCommand(() => {
                      navigateToRoute(pageRoute.path);
                    });
                  }}
                >
                  {Icon && <Icon className="mr-2 h-4 w-4" />}
                  {pageRoute.name}
                </CommandItem>
              );
            })}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
